import React from 'react';
import { ResourceModule } from '../../features/common/ResourceModule';
import { registerForms } from '../../features/common/formRegistry';
import { customersApi, subscriptionDayChangesApi, subscriptionPlansApi } from '../../services/resources';
import { formatDate } from '../../utils/format';
import { cn } from '../../utils/cn';
import type { Customer, SubscriptionDayChange, SubscriptionPlan } from '../../types';
import type { FieldConfig, ModuleConfig } from '../../features/common/types';

// The milk customers' side, on the Online Shop & Customers page (moved here
// from Procurement & Finance, on request): plans, the customers themselves
// and their day changes. Billing (Customer Accounts / Payments) is in
// procurement/CustomerBilling.tsx.

const activeBadge = (active: boolean) => (
  <span className={cn('inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full border',
    active ? 'bg-success-50 text-success-700 border-success-100' : 'bg-surface-muted text-ink-500 border-ink-300/30')}>
    {active ? 'Active' : 'Inactive'}
  </span>
);

const SHIFT_OPTIONS = [
  { value: 'Morning', label: 'Morning only' },
  { value: 'Evening', label: 'Evening only' },
  { value: 'Both', label: 'Both (split across the day)' },
];
const isBothShift = (v: Record<string, unknown>) => v.shift === 'Both';

const subscriptionPlansConfig: ModuleConfig<SubscriptionPlan> = {
  title: 'Subscription Plans', entityLabel: 'subscription plan', module: 'Sales', queryKey: 'subscription-plans', api: subscriptionPlansApi, idKey: 'id',
  canDelete: true,
  subtitle: 'Reusable daily-quantity/rate templates for Milk Sales subscriptions — assign one to a Customer/Partner/Vendor from its own form.',
  defaultValues: { active: true, shift: 'Morning' },
  columns: [
    { key: 'name', header: 'Name', primary: true, render: (r) => r.name },
    { key: 'litres', header: 'Litres/Day', align: 'right', render: (r) => r.litres_per_day },
    { key: 'shift', header: 'Shift', render: (r) => (r.shift === 'Both' ? `Both (${r.morning_litres ?? '?'} AM + ${r.evening_litres ?? '?'} PM)` : r.shift) },
    { key: 'rate', header: 'Rate/L (₹)', align: 'right', render: (r) => r.rate_per_litre },
    { key: 'active', header: 'Status', render: (r) => activeBadge(r.active) },
  ],
  fields: [
    { name: 'name', label: 'Plan Name', type: 'text', section: 'Details', required: true, placeholder: 'e.g. Daily 1.5L Partner Plan' },
    { name: 'litres_per_day', label: 'Litres / Day', type: 'decimal', section: 'Details', required: true, min: 0 },
    { name: 'rate_per_litre', label: 'Rate / Litre (₹)', type: 'decimal', section: 'Details', required: true, min: 0 },
    { name: 'shift', label: 'Delivered', type: 'select', section: 'Details', required: true, staticOptions: SHIFT_OPTIONS,
      helperText: 'Both splits the Litres/Day above across a Morning delivery and an Evening delivery.' },
    { name: 'morning_litres', label: 'Morning Litres', type: 'decimal', section: 'Details',
      visibleWhen: isBothShift, required: isBothShift, min: 0 },
    { name: 'evening_litres', label: 'Evening Litres', type: 'decimal', section: 'Details',
      visibleWhen: isBothShift, required: isBothShift, min: 0,
      helperText: 'Morning + Evening must add up to the Litres/Day total above.' },
    { name: 'active', label: 'Active', type: 'checkbox', section: 'Details' },
  ] as FieldConfig<any>[],
};

// A customer's day that differs from the plan - from the customer's own app,
// an Agent at the door, or added here. Applies to the delivery route at once.
const DAY_CHANGE_KINDS = [
  { value: 'pause', label: 'Pause (away - no milk, no charge)' },
  { value: 'move', label: 'Move the whole day to AM or PM' },
  { value: 'quantity', label: 'Different litres for the day' },
];
const isKind = (k: string) => (v: Record<string, unknown>) => v.kind === k;

const dayChangesConfig: ModuleConfig<SubscriptionDayChange> = {
  title: 'Delivery Changes', entityLabel: 'delivery change', module: 'Deliveries', queryKey: 'subscription-day-changes', api: subscriptionDayChangesApi, idKey: 'id',
  canDelete: true,
  subtitle: 'Paused days, AM/PM moves and one-day quantity changes still to come. Customers make these in their app; Agents at the door. Deleting one puts that day back to the plan.',
  addLabel: 'Add change',
  referenceKeys: ['customers'],
  defaultValues: { kind: 'pause' },
  columns: [
    { key: 'date', header: 'Date', render: (r) => formatDate(r.change_date) },
    { key: 'customer', header: 'Customer', primary: true, render: (r) => r.customer_name },
    { key: 'summary', header: 'Change', render: (r) => r.summary },
    { key: 'by', header: 'Made by', hideOnMobile: true, render: (r) => (r.requested_by_customer ? 'Customer (app)' : 'Staff') },
    { key: 'note', header: 'Note', hideOnTablet: true, render: (r) => r.note || '—' },
  ],
  deriveEditValues: (r) => ({
    kind: r.paused ? 'pause' : 'quantity', start_date: r.change_date,
    morning_litres: r.morning_litres ?? '', evening_litres: r.evening_litres ?? '',
  }),
  fields: [
    { name: 'customer_id', label: 'Customer', type: 'select', section: 'Change', required: true, optionsKey: 'customers' },
    { name: 'kind', label: 'Change', type: 'select', section: 'Change', required: true, staticOptions: DAY_CHANGE_KINDS },
    { name: 'start_date', label: 'Date (from)', type: 'date', section: 'Change', required: true },
    { name: 'end_date', label: 'Until (optional)', type: 'date', section: 'Change', optional: true, visibleWhen: isKind('pause'),
      helperText: 'Blank = just the one day. Up to 60 days.' },
    { name: 'to_shift', label: 'Deliver in', type: 'select', section: 'Change', visibleWhen: isKind('move'), required: isKind('move'),
      staticOptions: [{ value: 'Morning', label: 'AM (morning)' }, { value: 'Evening', label: 'PM (evening)' }] },
    { name: 'morning_litres', label: 'AM litres', type: 'decimal', section: 'Change', visibleWhen: isKind('quantity'), min: 0, optional: true },
    { name: 'evening_litres', label: 'PM litres', type: 'decimal', section: 'Change', visibleWhen: isKind('quantity'), min: 0, optional: true,
      helperText: '0 = no milk in that shift. Blank = as usual.' },
    { name: 'note', label: 'Note', type: 'text', section: 'Change', optional: true, fullWidth: true },
  ] as FieldConfig<any>[],
};

const isSubscribed = (v: Record<string, unknown>) => !!v.subscription_active;
const isPrepaid = (v: Record<string, unknown>) => v.billing_mode === 'Prepaid';
const isPostpaid = (v: Record<string, unknown>) => v.billing_mode === 'Postpaid';
const isCustomBothShift = (v: Record<string, unknown>) => isSubscribed(v) && v.subscription_shift === 'Both';

// A set_pin value never gets sent as part of the plain Customer payload -
// it goes through its own PUT /customers/{id}/pin call (see
// customersApi.setPin), the same way a Milk Session's photo is a second
// call after the record itself exists. Blank = leave whatever PIN (or
// lack of one) the customer already has untouched.
async function applyPinIfProvided(customerId: string, v: Record<string, unknown>): Promise<void> {
  const pin = (v.set_pin as string | undefined)?.trim();
  if (pin) await customersApi.setPin(customerId, pin);
}

const customersConfig: ModuleConfig<Customer> = {
  title: 'Customers / Partners / Vendors', entityLabel: 'customer', module: 'Sales', queryKey: 'customers', api: {
    list: customersApi.list,
    create: async (v: any) => {
      const created = await customersApi.create(v);
      await applyPinIfProvided(created.id, v);
      return created;
    },
    update: async (id: string, v: any) => {
      const updated = await customersApi.update(id, v);
      await applyPinIfProvided(id, v);
      return updated;
    },
    remove: customersApi.remove,
  }, idKey: 'id',
  canDelete: true,
  referenceKeys: ['subscriptionPlans', 'users'],
  defaultValues: {
    active: true, party_type: 'Customer', subscription_active: false,
    billing_mode: 'Postpaid', billing_cycle: 'Monthly', payment_due_days: 7,
  },
  columns: [
    { key: 'code', header: 'Code', render: (r) => r.customer_code || '—' },
    { key: 'name', header: 'Name', primary: true, render: (r) => r.name },
    { key: 'type', header: 'Type', render: (r) => r.party_type },
    { key: 'contact', header: 'Mobile', render: (r) => r.mobile || r.contact || '—' },
    { key: 'rate', header: 'Rate/L (₹)', align: 'right', render: (r) => (r.default_rate_per_litre != null ? r.default_rate_per_litre : '—') },
    { key: 'subscription', header: 'Subscription', render: (r) => (r.subscription_active ? 'Active' : '—') },
    { key: 'billing', header: 'Billing', render: (r) => (r.billing_mode === 'Prepaid' ? 'Prepaid' : `Postpaid · ${r.billing_cycle}`) },
    { key: 'pin', header: 'App Login', render: (r) => (r.has_pin ? 'PIN set' : '—') },
    { key: 'active', header: 'Status', render: (r) => activeBadge(r.active) },
  ],
  fields: [
    { name: 'party_type', label: 'Type', type: 'select', section: 'Details', required: true, staticOptions: [
      { value: 'Customer', label: 'Customer' }, { value: 'Partner', label: 'Partner' }, { value: 'Vendor', label: 'Vendor' },
    ] },
    { name: 'name', label: 'Name', type: 'text', section: 'Details', required: true },
    { name: 'mobile', label: 'Mobile Number', type: 'text', section: 'Details', optional: true },
    { name: 'email', label: 'Email ID', type: 'text', section: 'Details', optional: true },
    { name: 'tax_id', label: 'Tax ID', type: 'text', section: 'Details', optional: true },
    { name: 'default_rate_per_litre', label: 'Default Rate/Litre (₹)', type: 'decimal', section: 'Details', optional: true, min: 0,
      helperText: 'Used as the default rate for Milk Sales to this party — a Partner left blank here defaults to the standard Rs.40/litre.' },
    { name: 'apartment_name', label: 'Apartment Name', type: 'text', section: 'Address', optional: true },
    { name: 'house_number', label: 'House Number', type: 'text', section: 'Address', optional: true },
    { name: 'address', label: 'Address', type: 'textarea', section: 'Address', optional: true, fullWidth: true },
    { name: 'city', label: 'City', type: 'text', section: 'Address', optional: true },
    { name: 'state', label: 'State', type: 'text', section: 'Address', optional: true },
    { name: 'pincode', label: 'Pincode', type: 'text', section: 'Address', optional: true },
    { name: 'active', label: 'Active', type: 'checkbox', section: 'Details' },
    { name: 'subscription_active', label: 'Milk Sales Subscription', type: 'checkbox', section: 'Subscription',
      helperText: 'When on, this party shows up on the Milk Sales tab for a daily Yes/No confirmation instead of a manual entry.' },
    { name: 'subscription_plan_id', label: 'Plan', type: 'select', optionsKey: 'subscriptionPlans', section: 'Subscription',
      visibleWhen: isSubscribed, optional: true, helperText: 'Manage plans from the Subscription Plans tab.' },
    { name: 'subscription_litres_per_day', label: 'Litres / Day (override)', type: 'decimal', section: 'Subscription',
      visibleWhen: isSubscribed, optional: true, min: 0, helperText: 'Blank = use the plan\'s own figure.' },
    { name: 'subscription_rate_per_litre', label: 'Rate / Litre (₹) (override)', type: 'decimal', section: 'Subscription',
      visibleWhen: isSubscribed, optional: true, min: 0, helperText: 'Blank = use the plan\'s own figure.' },
    { name: 'subscription_shift', label: 'Shift (override)', type: 'select', optional: true, section: 'Subscription',
      visibleWhen: isSubscribed, staticOptions: SHIFT_OPTIONS, helperText: 'Blank = use the plan\'s own Shift (and its split, if Both).' },
    { name: 'subscription_morning_litres', label: 'Morning Litres', type: 'decimal', section: 'Subscription',
      visibleWhen: isCustomBothShift, required: isCustomBothShift, min: 0 },
    { name: 'subscription_evening_litres', label: 'Evening Litres', type: 'decimal', section: 'Subscription',
      visibleWhen: isCustomBothShift, required: isCustomBothShift, min: 0,
      helperText: 'Morning + Evening must add up to the day\'s total Litres (override above, or the plan\'s own figure).' },
    { name: 'subscription_start_date', label: 'Start Date', type: 'date', section: 'Subscription',
      visibleWhen: isSubscribed, required: isSubscribed },
    { name: 'subscription_valid_till', label: 'Valid Till', type: 'date', section: 'Subscription',
      visibleWhen: isSubscribed, optional: true, helperText: 'Blank = open-ended.' },
    // Delivery app (added on request, Phase 2 of the mobile app) - who
    // physically delivers to this customer, the geofence anchor their
    // Offline Bypass deliveries are checked against, and the PIN they use
    // to sign into their own Customer app (set_pin goes through its own
    // PUT /customers/{id}/pin call, not the plain Customer payload - see
    // applyPinIfProvided above - since the hash never round-trips back
    // out to prefill this field; it's always blank until typed).
    { name: 'assigned_agent_id', label: 'Delivery Agent', type: 'select', optionsKey: 'users', section: 'Delivery',
      optional: true, helperText: 'Which Agent delivers to this customer (agent web page or mobile app) — left blank, any Agent can see and deliver to them.' },
    { name: 'registered_latitude', label: 'Registered Latitude', type: 'decimal', section: 'Delivery',
      optional: true, helperText: 'The customer\'s home/delivery address coordinates — an Offline Bypass delivery more than 50m from here is flagged for review.' },
    { name: 'registered_longitude', label: 'Registered Longitude', type: 'decimal', section: 'Delivery', optional: true },
    { name: 'set_pin', label: 'Set App Login PIN', type: 'text', section: 'Delivery', optional: true,
      helperText: 'The PIN this customer uses to sign in (website account or app, with their Mobile Number above). Leave blank to keep their current PIN unchanged.' },
    // Billing - picked per customer at enrolment, on their request. Rules
    // live in the backend's app/services/billing_service.py.
    { name: 'billing_mode', label: 'Billing Mode', type: 'select', section: 'Billing', required: true, staticOptions: [
      { value: 'Prepaid', label: 'Prepaid — wallet, every delivery deducted' },
      { value: 'Postpaid', label: 'Postpaid — pay later, billed per cycle' },
    ], helperText: 'Prepaid: the customer tops up first; each delivery is deducted and they and their Agent are warned when the wallet runs low. Postpaid: deliveries are billed per cycle and must be paid by the due date.' },
    { name: 'low_balance_threshold', label: 'Low-Balance Warning At (₹)', type: 'decimal', section: 'Billing',
      visibleWhen: isPrepaid, optional: true, min: 0,
      helperText: 'Warn the customer and Agent once the wallet falls to this amount. Blank = 3 days of this customer\'s subscription.' },
    { name: 'billing_cycle', label: 'Billing Cycle', type: 'select', section: 'Billing',
      visibleWhen: isPostpaid, required: isPostpaid, staticOptions: [
        { value: 'Weekly', label: 'Weekly (Mon–Sun)' }, { value: 'Monthly', label: 'Monthly (calendar month)' },
      ] },
    { name: 'payment_due_days', label: 'Payment Due (days after cycle ends)', type: 'number', section: 'Billing',
      visibleWhen: isPostpaid, required: isPostpaid, min: 0,
      helperText: 'e.g. 7 on a Monthly cycle = September\'s bill is due 7 October; unpaid after that it shows as Overdue (0–60).' },
  ] as FieldConfig<any>[],
};

export const SubscriptionPlansTab = () => <ResourceModule config={subscriptionPlansConfig} />;
export const CustomersTab = () => <ResourceModule config={customersConfig} />;
export const DeliveryChangesTab = () => <ResourceModule config={dayChangesConfig} />;

// For Administration -> Entry Form Settings.
registerForms('Online Shop & Customers', subscriptionPlansConfig, customersConfig, dayChangesConfig);
